import { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Microphone, MicrophoneSlash, PhoneCall, PhoneX, Headphones, Waveform } from "@phosphor-icons/react";

const ICE = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };

const VoiceChat = forwardRef(function VoiceChat({ user }, ref) {
  const [joined, setJoined] = useState(false);
  const [muted, setMuted] = useState(false);
  const [voice, setVoice] = useState({});
  const [speaking, setSpeaking] = useState({});
  const streamRef = useRef(null);
  const pcsRef = useRef({});
  const audioRoot = useRef(null);
  const joinedRef = useRef(false);
  const ctxRef = useRef(null);
  const analysersRef = useRef({});
  const meterRef = useRef(null);
  joinedRef.current = joined;

  const signal = (to, data) => api.post("/watch/voice/signal", { to, data }).catch(() => {});

  const attachAnalyser = (uid, stream) => {
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current.resume?.();
      const analyser = ctxRef.current.createAnalyser();
      analyser.fftSize = 256;
      ctxRef.current.createMediaStreamSource(stream).connect(analyser);
      analysersRef.current[uid] = { analyser, data: new Uint8Array(analyser.fftSize) };
    } catch {}
  };

  const startMeter = () => {
    if (meterRef.current) return;
    meterRef.current = setInterval(() => {
      const next = {};
      for (const [uid, { analyser, data }] of Object.entries(analysersRef.current)) {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += Math.abs(data[i] - 128);
        next[uid] = sum / data.length > 6;
      }
      setSpeaking((prev) => {
        const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
        for (const k of keys) if (!!prev[k] !== !!next[k]) return next;
        return prev;
      });
    }, 160);
  };

  const stopMeter = () => {
    clearInterval(meterRef.current);
    meterRef.current = null;
    analysersRef.current = {};
    setSpeaking({});
    try { ctxRef.current?.close(); } catch {}
    ctxRef.current = null;
  };

  const closePeer = (uid) => {
    const pc = pcsRef.current[uid];
    if (pc) { try { pc.close(); } catch {} delete pcsRef.current[uid]; }
    delete analysersRef.current[uid];
    document.getElementById(`voice-audio-${uid}`)?.remove();
  };

  const createPeer = useCallback((uid, initiator) => {
    closePeer(uid);
    const pc = new RTCPeerConnection(ICE);
    pcsRef.current[uid] = pc;
    streamRef.current?.getTracks().forEach((t) => pc.addTrack(t, streamRef.current));
    pc.onicecandidate = (e) => { if (e.candidate) signal(uid, { type: "ice", candidate: e.candidate.toJSON() }); };
    pc.ontrack = (e) => {
      let el = document.getElementById(`voice-audio-${uid}`);
      if (!el) {
        el = document.createElement("audio");
        el.id = `voice-audio-${uid}`;
        el.autoplay = true;
        audioRoot.current?.appendChild(el);
      }
      el.srcObject = e.streams[0];
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed"].includes(pc.connectionState)) closePeer(uid);
    };
    if (initiator) {
      pc.onnegotiationneeded = async () => {
        try {
          await pc.setLocalDescription(await pc.createOffer());
          signal(uid, { type: "offer", sdp: pc.localDescription.sdp });
        } catch {}
      };
    }
    return pc;
  }, []);

  const join = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      return toast.error("Нет доступа к микрофону — разрешите его в браузере");
    }
    try {
      const { data } = await api.post("/watch/voice/join");
      setVoice(data.voice);
      setJoined(true);
      setMuted(false);
      attachAnalyser(user.id, streamRef.current);
      startMeter();
      data.peers.forEach((uid) => createPeer(uid, true));
      toast.success("Вы в голосовом чате");
    } catch (e) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      toast.error(formatApiError(e));
    }
  };

  const leave = useCallback((silent = false) => {
    Object.keys(pcsRef.current).forEach(closePeer);
    stopMeter();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setJoined(false);
    setVoice((v) => { const c = { ...v }; delete c[user.id]; return c; });
    if (!silent) api.post("/watch/voice/leave").catch(() => {});
  }, [user.id]);

  const toggleMute = () => {
    const next = !muted;
    streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  useImperativeHandle(ref, () => ({
    async onEvent(d) {
      if (d.event === "voice") {
        setVoice(d.voice || {});
        if (d.left) closePeer(d.left);
        return;
      }
      if (d.event === "voice-signal" && joinedRef.current) {
        const { from, data } = d;
        let pc = pcsRef.current[from];
        try {
          if (data.type === "offer") {
            pc = createPeer(from, false);
            await pc.setRemoteDescription({ type: "offer", sdp: data.sdp });
            await pc.setLocalDescription(await pc.createAnswer());
            signal(from, { type: "answer", sdp: pc.localDescription.sdp });
          } else if (data.type === "answer" && pc) {
            await pc.setRemoteDescription({ type: "answer", sdp: data.sdp });
          } else if (data.type === "ice" && pc) {
            await pc.addIceCandidate(data.candidate);
          }
        } catch {}
      }
    },
  }), [createPeer]);

  // fetch current voice presence on mount; leave on unmount/close
  useEffect(() => {
    api.get("/watch/room").then((r) => setVoice(r.data.room.voice || {})).catch(() => {});
    const bye = () => { if (joinedRef.current) navigator.sendBeacon?.(`${process.env.REACT_APP_BACKEND_URL}/api/watch/voice/leave`); };
    window.addEventListener("beforeunload", bye);
    return () => {
      window.removeEventListener("beforeunload", bye);
      if (joinedRef.current) leave();
    };
  }, [leave]);

  const names = Object.entries(voice);

  return (
    <div className="glass-card p-3 flex items-center gap-2 flex-wrap" data-testid="voice-chat">
      <div ref={audioRoot} className="hidden" />
      {!joined ? (
        <Button size="sm" onClick={join} data-testid="voice-join-btn">
          <PhoneCall size={15} className="mr-1.5" weight="fill" />Войти в голосовой чат
        </Button>
      ) : (
        <>
          <Button size="sm" variant={muted ? "destructive" : "default"} onClick={toggleMute} data-testid="voice-mute-btn">
            {muted ? <MicrophoneSlash size={15} className="mr-1.5" /> : <Microphone size={15} className="mr-1.5" weight="fill" />}
            {muted ? "Микрофон выкл" : "Микрофон вкл"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => leave()} data-testid="voice-leave-btn">
            <PhoneX size={15} className="mr-1.5" />Выйти
          </Button>
        </>
      )}
      <div className="flex items-center gap-1.5 ml-auto flex-wrap" data-testid="voice-members">
        {names.length === 0 ? (
          <span className="text-[11px] text-muted-foreground">Обсуждайте серию голосом, не печатая</span>
        ) : names.map(([uid, name]) => {
          const talk = speaking[uid] && !(uid === user?.id && muted);
          return (
            <span key={uid} data-testid={`voice-member-${uid}`}
              className={`flex items-center gap-1 text-xs rounded-full px-2 py-0.5 transition-all duration-150 ${
                talk ? "bg-[#00e676]/25 text-[#00e676] ring-2 ring-[#00e676]/60 scale-105" : "bg-primary/15 text-primary"}`}>
              {talk ? <Waveform size={12} weight="fill" className="animate-pulse" /> : <Headphones size={12} weight="fill" />}
              {name}{uid === user?.id ? " (вы)" : ""}
            </span>
          );
        })}
      </div>
    </div>
  );
});

export default VoiceChat;
